import { type Dispatch, type SetStateAction } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import useCreateEventSessionMutation from '@/state/mutations/sessions/use-create-event-session.ts';
import { type SessionType } from '@/types/dtos/sessions/session-type.enum.ts';
import AddSessionForm, { type SessionFormType } from './add-session.form';
import { parse } from 'date-fns';
import { useSearchParams } from 'react-router';

const AddSessionModal = ({ open, setOpen }: { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }) => {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const { setSelectedSession } = useEventStore();
  const { mutateAsync, isPending } = useCreateEventSessionMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  const onSubmit = async (data: SessionFormType) => {
    if (!event?.id) {
      notification.notifyError(t('session.notification.createError'));
      return;
    }

    try {
      const plannedDurationMinutes = parse(data.plannedDurationMinutes, 'HH:mm', new Date());
      const res = await mutateAsync({
        eventId: event.id,
        session: {
          title: data.title,
          type: data.type as SessionType,
          plannedDurationMinutes: plannedDurationMinutes.getHours() * 60 + plannedDurationMinutes.getMinutes(),
          isInLibrary: data.isInLibrary,
        },
      });
      notification.notifySuccess(t('session.notification.createSuccess', { title: res.title }));
      setSelectedSession(res);
      searchParams.set('key', res.id);
      setSearchParams(searchParams);
      setOpen(false);
    } catch {
      notification.notifyError(t('session.notification.createError'));
    }
  };

  return (
    <Modal
      open={open}
      data-testid="add-session-modal"
      handleClose={() => setOpen(false)}
      title={t('session.createModal.title')}
      description={t('session.createModal.description')}
      size="sm"
    >
      <AddSessionForm onSubmit={onSubmit} onCancel={() => setOpen(false)} isLoading={isPending} defaultIsInLibrary />
    </Modal>
  );
};

export default AddSessionModal;
